/**
 * Ambient Poller — periodically reports desktop state to the server
 * for activity stream tracking and proactive trigger evaluation.
 */
import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
// Dynamic import to avoid crash in non-Tauri environments (mobile/web)
// const invoke = await import('@tauri-apps/api/core').then(m => m.invoke);
import { isTauriRuntime } from '@/services/apiBridge';

const isTauri = isTauriRuntime();

interface ActivitySnapshot {
  window: { title: string; process_name: string; pid: number };
  idle: { idle_ms: number; idle_seconds: number };
}

let lastWindowTitle = '';
let lastWindowProc = '';
let lastClipboardText = '';

export function useAmbientPoller(socket: Socket | null) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!socket || !isTauri) return;

    // Poll window activity every 5s
    const pollWindow = async () => {
      try {
        const snap = await invoke<ActivitySnapshot>('poll_activity');
        const { window: win, idle } = snap;

        // Emit idle report
        socket.emit('ambient:idle_report', idle);

        // Emit window update if changed
        if (win.title !== lastWindowTitle || win.process_name !== lastWindowProc) {
          lastWindowTitle = win.title;
          lastWindowProc = win.process_name;
          socket.emit('ambient:window_update', win);
        }
      } catch {
        // Tauri commands not available (web mode or app not ready)
      }
    };

    // Poll clipboard every 3s
    const pollClipboard = async () => {
      try {
        const text = await invoke<string>('get_clipboard_text');
        if (text !== lastClipboardText) {
          lastClipboardText = text;
          socket.emit('ambient:clipboard_report', { text });
        }
      } catch {
        // Tauri clipboard not available
      }
    };

    pollWindow(); // initial poll
    pollClipboard();

    const id = setInterval(() => {
      pollWindow();
      pollClipboard();
    }, 5000);
    intervalRef.current = id;

    return () => {
      clearInterval(id);
    };
  }, [socket]);
}
